import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search,
  Clear,
  CreateNewFolder,
  UploadFile,
  Edit,
  Delete,
  Folder,
  FolderOpen,
  BrandingWatermark,
  Lock,
  Public,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CorporateDocumentService } from 'CorporateDocument/Domain/Services/CorporateDocumentService';

export default function AdminCorporateDocumentsView() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal Category state
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    parentId: '',
    isWatermark: true,
    departmentIds: [],
    jobPositionIds: [],
  });

  // Modal Upload Document state
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadCategoryId, setUploadCategoryId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocName, setUploadDocName] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Delete Category state
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);

  // Load all categories, departments, and job positions
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await CorporateDocumentService.getAdminCategories();
      if (res) {
        setCategories(res.categories || []);
        setDepartments(res.departments || []);
        setJobPositions(res.jobPositions || []);
      }
    } catch (err) {
      toast.error('Error al cargar la información de administración.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open modal to create a new category
  const handleOpenCreateCategory = (parentId = '') => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      parentId: parentId || '',
      isWatermark: true,
      departmentIds: [],
      jobPositionIds: [],
    });
    setOpenCategoryModal(true);
  };

  // Open modal to edit an existing category
  const handleOpenEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      parentId: cat.parentId || '',
      isWatermark: cat.isWatermark,
      departmentIds: cat.departmentIds || [],
      jobPositionIds: cat.jobPositionIds || [],
    });
    setOpenCategoryModal(true);
  };

  // Save Category (create or update)
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.warn('El nombre de la categoría es obligatorio.');
      return;
    }

    try {
      setSavingCategory(true);
      if (editingCategory) {
        await CorporateDocumentService.updateCategory(editingCategory.id, categoryForm);
        toast.success('Categoría actualizada correctamente.');
      } else {
        await CorporateDocumentService.createCategory(categoryForm);
        toast.success('Categoría creada correctamente.');
      }
      setOpenCategoryModal(false);
      loadData();
    } catch (err) {
      toast.error('Error al guardar la categoría.');
    } finally {
      setSavingCategory(false);
    }
  };

  // Delete Category
  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      setDeletingCategory(true);
      await CorporateDocumentService.deleteCategory(categoryToDelete.id);
      toast.success('Categoría eliminada.');
      setCategoryToDelete(null);
      loadData();
    } catch (err) {
      toast.error('Error al eliminar la categoría.');
    } finally {
      setDeletingCategory(false);
    }
  };

  // Open modal to upload document
  const handleOpenUploadModal = (categoryId = '') => {
    setUploadCategoryId(categoryId || (categories.length > 0 ? categories[0].id : ''));
    setUploadFile(null);
    setUploadDocName('');
    setOpenUploadModal(true);
  };

  // Handle file select
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      if (!uploadDocName.trim()) {
        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        setUploadDocName(nameWithoutExt);
      }
    }
  };

  // Save Uploaded Document
  const handleSaveUpload = async () => {
    if (!uploadFile) {
      toast.warn('Por favor, selecciona un archivo para subir.');
      return;
    }
    if (!uploadCategoryId) {
      toast.warn('Selecciona la carpeta de destino.');
      return;
    }

    try {
      setUploadingDoc(true);
      await CorporateDocumentService.uploadDocument(uploadFile, uploadDocName, uploadCategoryId);
      toast.success('Documento subido con éxito.');
      setOpenUploadModal(false);
      loadData();
    } catch (err) {
      toast.error('Error al subir el documento.');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categories;
    const term = searchTerm.toLowerCase();
    return categories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(term) ||
        (cat.parentName && cat.parentName.toLowerCase().includes(term))
    );
  }, [categories, searchTerm]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1600, margin: '0 auto' }}>
      {/* Top Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Gestión de Documentos Corporativos
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>
            Administra la estructura de carpetas, activa marcas de agua y configura permisos por departamento o puesto.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolder />}
            onClick={() => handleOpenCreateCategory()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#257eb9',
              color: '#257eb9',
              '&:hover': { borderColor: '#1b6393', bgcolor: '#f0f7fd' },
            }}
          >
            Nueva Carpeta
          </Button>

          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={() => handleOpenUploadModal()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#257eb9',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1b6393', boxShadow: 'none' },
            }}
          >
            Subir Documento
          </Button>
        </Box>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
              Total Carpetas
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
              {categories.length}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
              Carpetas Raíz
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#257eb9', mt: 0.5 }}>
              {categories.filter((c) => !c.parentId).length}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
              Con Marca de Agua
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0284c7', mt: 0.5 }}>
              {categories.filter((c) => c.isWatermark).length}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
              Con Permisos Restringidos
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#7c3aed', mt: 0.5 }}>
              {categories.filter((c) => (c.departmentIds?.length > 0 || c.jobPositionIds?.length > 0)).length}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        {/* Search */}
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            size="small"
            placeholder="Filtrar carpetas por nombre o carpeta padre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 450, width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { borderRadius: '8px', bgcolor: '#ffffff' },
            }}
          />
        </Box>

        {/* Table */}
        <TableContainer sx={{ maxHeight: '65vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Nombre de la Carpeta</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Carpeta Padre</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Marca de Agua</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Visibilidad / Permisos</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Docs</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} sx={{ color: '#257eb9' }} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b' }}>
                      Cargando carpetas...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#64748b' }}>
                    No se encontraron carpetas.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((cat) => {
                  const isRestricted = (cat.departmentIds?.length > 0 || cat.jobPositionIds?.length > 0);

                  return (
                    <TableRow key={cat.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      {/* Name */}
                      <TableCell sx={{ fontWeight: cat.parentId ? 500 : 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {cat.parentId ? (
                            <FolderOpen sx={{ color: '#64748b', fontSize: '1.2rem', ml: 1 }} />
                          ) : (
                            <Folder sx={{ color: '#257eb9', fontSize: '1.3rem' }} />
                          )}
                          <span>{cat.name}</span>
                        </Box>
                      </TableCell>

                      {/* Parent */}
                      <TableCell>
                        {cat.parentName ? (
                          <Chip label={cat.parentName} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            Raíz
                          </Typography>
                        )}
                      </TableCell>

                      {/* Watermark */}
                      <TableCell>
                        {cat.isWatermark ? (
                          <Chip
                            icon={<BrandingWatermark sx={{ fontSize: '0.9rem !important' }} />}
                            label="Activa"
                            size="small"
                            sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.75rem' }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Desactivada
                          </Typography>
                        )}
                      </TableCell>

                      {/* Permissions */}
                      <TableCell sx={{ maxWidth: 350 }}>
                        {!isRestricted ? (
                          <Chip
                            icon={<Public sx={{ fontSize: '0.9rem !important' }} />}
                            label="Público (Todo el mundo)"
                            size="small"
                            variant="outlined"
                            sx={{ color: '#16a34a', borderColor: '#bbf7d0', bgcolor: '#f0fdf4', fontSize: '0.75rem' }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              icon={<Lock sx={{ fontSize: '0.85rem !important' }} />}
                              label="Restringido"
                              size="small"
                              sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 600, fontSize: '0.72rem' }}
                            />
                            {cat.departments?.map((d) => (
                              <Chip
                                key={`dept-${d.id}`}
                                label={d.name}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                            {cat.jobPositions?.map((jp) => (
                              <Chip
                                key={`jp-${jp.id}`}
                                label={jp.name}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>

                      {/* Document count */}
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                          {cat.documentCount}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Subir documento a esta carpeta">
                            <IconButton size="small" onClick={() => handleOpenUploadModal(cat.id)}>
                              <UploadFile fontSize="small" sx={{ color: '#257eb9' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Crear subcarpeta aquí">
                            <IconButton size="small" onClick={() => handleOpenCreateCategory(cat.id)}>
                              <CreateNewFolder fontSize="small" sx={{ color: '#0284c7' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Editar carpeta">
                            <IconButton size="small" onClick={() => handleOpenEditCategory(cat)}>
                              <Edit fontSize="small" sx={{ color: '#d97706' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar carpeta">
                            <IconButton size="small" onClick={() => setCategoryToDelete(cat)}>
                              <Delete fontSize="small" sx={{ color: '#e11d48' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* DIALOG: CREATE / EDIT CATEGORY */}
      <Dialog
        open={openCategoryModal}
        onClose={() => !savingCategory && setOpenCategoryModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          {editingCategory ? 'Editar Carpeta' : 'Nueva Carpeta o Subcarpeta'}
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2.5 }}>
          <TextField
            label="Nombre de la carpeta"
            fullWidth
            required
            value={categoryForm.name}
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            placeholder="Ej: Catálogos de Cabina, Manuales de Instalación..."
          />

          <FormControl fullWidth>
            <InputLabel id="parent-cat-label">Carpeta Padre (Opcional)</InputLabel>
            <Select
              labelId="parent-cat-label"
              value={categoryForm.parentId}
              label="Carpeta Padre (Opcional)"
              onChange={(e) => setCategoryForm({ ...categoryForm, parentId: e.target.value })}
            >
              <MenuItem value="">
                <em>Ninguna (Categoría raíz principal)</em>
              </MenuItem>
              {categories
                .filter((c) => !editingCategory || c.id !== editingCategory.id)
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.parentName ? `${c.parentName} / ${c.name}` : c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Watermark checkbox */}
          <Box
            sx={{
              p: 2,
              borderRadius: '10px',
              bgcolor: '#f0f7fd',
              border: '1px solid #b9ddf8',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={categoryForm.isWatermark}
                  onChange={(e) => setCategoryForm({ ...categoryForm, isWatermark: e.target.checked })}
                  sx={{ color: '#257eb9', '&.Mui-checked': { color: '#257eb9' } }}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0369a1' }}>
                    Activar marca de agua
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#475569' }}>
                    Añade automáticamente una marca de agua de seguridad al visualizar o descargar documentos de esta carpeta.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Department Permissions */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', mb: 0.8 }}>
              Restricción por Departamento
            </Typography>
            <Autocomplete
              multiple
              options={departments}
              getOptionLabel={(option) => option.name}
              value={departments.filter((d) => categoryForm.departmentIds.includes(d.id))}
              onChange={(event, newValue) => {
                setCategoryForm({
                  ...categoryForm,
                  departmentIds: newValue.map((v) => v.id),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Seleccionar departamentos..."
                  helperText="Deja vacío para que sea accesible para todos los departamentos."
                />
              )}
            />
          </Box>

          {/* Job Position Permissions */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', mb: 0.8 }}>
              Restricción por Puesto de Trabajo
            </Typography>
            <Autocomplete
              multiple
              options={jobPositions}
              getOptionLabel={(option) => option.name}
              value={jobPositions.filter((jp) => categoryForm.jobPositionIds.includes(jp.id))}
              onChange={(event, newValue) => {
                setCategoryForm({
                  ...categoryForm,
                  jobPositionIds: newValue.map((v) => v.id),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Seleccionar puestos..."
                  helperText="Deja vacío para que sea accesible para todos los puestos."
                />
              )}
            />
          </Box>

          <Alert severity="info" sx={{ borderRadius: '8px', fontSize: '0.82rem' }}>
            <strong>Nota:</strong> Si no seleccionas ningún departamento ni puesto de trabajo, la carpeta será accesible para <strong>todo el mundo</strong> por defecto.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCategoryModal(false)} disabled={savingCategory} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={savingCategory}
            startIcon={savingCategory ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              bgcolor: '#257eb9',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1b6393' },
            }}
          >
            {savingCategory ? 'Guardando...' : editingCategory ? 'Guardar Cambios' : 'Crear Carpeta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: UPLOAD DOCUMENT */}
      <Dialog
        open={openUploadModal}
        onClose={() => !uploadingDoc && setOpenUploadModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Subir Documento
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2.5 }}>
          <FormControl fullWidth required>
            <InputLabel id="upload-cat-label">Carpeta de Destino</InputLabel>
            <Select
              labelId="upload-cat-label"
              value={uploadCategoryId}
              label="Carpeta de Destino"
              onChange={(e) => setUploadCategoryId(e.target.value)}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.parentName ? `${c.parentName} / ${c.name}` : c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', mb: 1 }}>
              Archivo a Subir (PDF, Word, Excel, Imagen, etc. Máx 50MB)
            </Typography>
            <input
              type="file"
              id="admin-doc-file-input"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <label htmlFor="admin-doc-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFile />}
                sx={{
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  py: 2.5,
                  width: '100%',
                  textTransform: 'none',
                  borderColor: uploadFile ? '#16a34a' : '#94a3b8',
                  bgcolor: uploadFile ? '#f0fdf4' : '#fafafa',
                  color: uploadFile ? '#15803d' : '#475569',
                }}
              >
                {uploadFile ? `Archivo seleccionado: ${uploadFile.name}` : 'Haz clic para seleccionar un archivo del equipo'}
              </Button>
            </label>
          </Box>

          <TextField
            label="Título / Nombre del Documento"
            fullWidth
            required
            value={uploadDocName}
            onChange={(e) => setUploadDocName(e.target.value)}
            placeholder="Ej: Ficha Técnica Cabina City, Catálogo Ascensores 2026..."
            helperText="Nombre con el que los usuarios verán este documento en el árbol."
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenUploadModal(false)} disabled={uploadingDoc} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveUpload}
            variant="contained"
            disabled={uploadingDoc || !uploadFile}
            startIcon={uploadingDoc ? <CircularProgress size={18} color="inherit" /> : <UploadFile />}
            sx={{
              bgcolor: '#257eb9',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1b6393' },
            }}
          >
            {uploadingDoc ? 'Subiendo...' : 'Subir Documento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: CONFIRM DELETE */}
      <Dialog
        open={Boolean(categoryToDelete)}
        onClose={() => !deletingCategory && setCategoryToDelete(null)}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#e11d48' }}>
          ¿Eliminar carpeta?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            ¿Estás seguro de que deseas eliminar la carpeta <strong>"{categoryToDelete?.name}"</strong>?
            Sus documentos y subcarpetas quedarán también ocultos para los usuarios.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCategoryToDelete(null)} disabled={deletingCategory} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteCategory}
            variant="contained"
            color="error"
            disabled={deletingCategory}
            startIcon={deletingCategory ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deletingCategory ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
